// Remembers where a list was scrolled to, so that navigating back to it from a
// detail page returns the user to the same row instead of the top.
//
// The list tables unmount when the router swaps in a detail route, so the
// scroll offset has to be stashed outside the component tree. It is deliberately
// in-memory only: a refresh should start clean, and keeping it here means the
// record can be updated cheaply on every scroll event.

const positions = new Map();

// Resolved by style alone, never by whether the element happens to be
// overflowing right now: a list page has more than one scrollable ancestor, so
// an "is it overflowing" test can pick a different element at save time than at
// restore time and file the offset against the wrong node.
const findScroller = (el) => {
  for (let node = el; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
};

export const scrollKey = (location) => location.pathname + location.search;

// Called from the scroller's own scroll event, so the element is known to be
// the right one and known to be scrollable. This is the path that matters.
export const trackScroll = (key, el) => {
  if (key && el) positions.set(key, el.scrollTop);
};

// Teardown fallback, for a layout where the scroller is an ancestor of the
// element we hold a ref to and no scroll event reaches us.
export const saveScroll = (key, el) => {
  if (!key || !el) return;
  const scroller = findScroller(el);
  if (!scroller) return;
  // A list that cannot scroll has no position worth recording, and the 0 it
  // reports would erase whatever the scroll handler already stored.
  if (scroller.scrollHeight <= scroller.clientHeight) return;
  positions.set(key, scroller.scrollTop);
};

// Returns true once there is nothing left to do, so callers can stop retrying.
export const restoreScroll = (key, el, action) => {
  // Only a back/forward press should land mid-list; opening the list afresh
  // from the nav bar starts at the top.
  if (action !== "POP") return true;
  const top = positions.get(key);
  if (!top) return true;
  const scroller = el && findScroller(el);
  if (!scroller) return false;
  // The offset is in place and survived a render, so we are done.
  if (scroller.scrollTop === top) return true;
  // Anywhere other than the top means the user has taken over; never fight them.
  if (scroller.scrollTop !== 0) return true;
  // Rows are not painted yet, so scrollTop would be clamped. Retry after the
  // next render.
  if (scroller.scrollHeight - scroller.clientHeight < top) return false;
  scroller.scrollTop = top;
  // Deliberately not done yet. A list page mounts its table before its own
  // componentDidMount decides whether to refetch, and a refetch swaps the rows
  // for a short placeholder list, which makes the browser clamp this offset
  // back to 0. Keep watch until it holds.
  return false;
};
